import { datadogLogs } from "@datadog/browser-logs";
import type { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { RedoFulfillmentTote } from "@redotech/redo-model/fulfillments/fulfillment-tote";
import { Team } from "@redotech/redo-model/team";
import {
  PickSession,
  PickSessionItem,
  PickSessionStrategy,
} from "@redotech/redo-model/wms/schema/pick-session";
import { toast } from "@redotech/redo-web/alert";
import {
  ERROR_SOUND,
  SUCCESS_SOUND,
  SUCCESS_SOUND_2,
} from "@redotech/redo-web/sound/sounds";

export const SCAN_CHAR = "&";

export const playSuccessSound = async () => {
  SUCCESS_SOUND.currentTime = 0;
  void SUCCESS_SOUND.play();
};

export const playSuccessSound2 = async () => {
  SUCCESS_SOUND_2.currentTime = 0;
  void SUCCESS_SOUND_2.play();
};

export const playErrorSound = async () => {
  ERROR_SOUND.currentTime = 0;
  void ERROR_SOUND.play();
};

// Hack for FP for now. Update when needed to be more generic.
export function getPickTitle(
  item:
    | PickSessionItem
    | FulfillmentGroupData["items"][number]
    | FulfillmentOrderLineItem,
) {
  const size = item.customAttributes.find((ca) =>
    ca.key.toLowerCase().includes("size"),
  );
  const color = item.customAttributes.find((ca) =>
    ca.key.toLowerCase().includes("color"),
  );

  const pickTitle =
    [size, color]
      .filter((c) => c !== undefined)
      .map((c) => `${c.key}: ${c.value}`)
      .join(" | ") || item.variantTitle;

  return pickTitle;
}

export function isItemMatch(
  item:
    | PickSessionItem
    | FulfillmentGroupData["items"][number]
    | FulfillmentOrderLineItem,
  inputBarcode: string,
  team?: Team | null,
): boolean {
  datadogLogs.logger.info("isItemMatch", { item, inputBarcode });
  if (
    item.barcode?.toUpperCase() === inputBarcode.toUpperCase() ||
    item.sku?.toUpperCase() === inputBarcode.toUpperCase() ||
    item.productTitle?.toUpperCase() === inputBarcode.toUpperCase() ||
    `I-${item.variantId}` === inputBarcode.toUpperCase() // specific to CS. CS barcodes are sometimes printed as i-{variantId} even though the variant barcode is a different value.
  ) {
    return true;
  }

  // Allow scanning custom attributes (sometimes extra barcodes are stored in custom attributes)
  if (
    item.customAttributes.some(
      (attr) => attr.value.toUpperCase() === inputBarcode.toUpperCase(),
    )
  ) {
    return true;
  }

  const productCustomMetadata =
    team?.settings?.outboundLabels?.productCustomMetadata;
  if (productCustomMetadata) {
    if (
      productCustomMetadata.productCustomString1?.scannable &&
      item.productCustomString1 &&
      item.productCustomString1.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      productCustomMetadata.productCustomString2?.scannable &&
      item.productCustomString2 &&
      item.productCustomString2.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      productCustomMetadata.productCustomString3?.scannable &&
      item.productCustomString3 &&
      item.productCustomString3.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
  }

  const variantCustomMetadata =
    team?.settings?.outboundLabels?.variantCustomMetadata;
  if (variantCustomMetadata) {
    if (
      variantCustomMetadata.variantCustomString1?.scannable &&
      item.variantCustomString1 &&
      item.variantCustomString1.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      variantCustomMetadata.variantCustomString2?.scannable &&
      item.variantCustomString2 &&
      item.variantCustomString2.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
    if (
      variantCustomMetadata.variantCustomString3?.scannable &&
      item.variantCustomString3 &&
      item.variantCustomString3.toUpperCase() === inputBarcode.toUpperCase()
    ) {
      return true;
    }
  }

  return false;
}

export async function getToteByBarcode({
  scannedValue,
  rpcClient,
}: {
  scannedValue: string;
  rpcClient: RedoOutboundLabelsRpcClient;
}): Promise<RedoFulfillmentTote | null> {
  let tote: RedoFulfillmentTote | null = null;
  try {
    tote = await rpcClient.getToteByBarcode({ barcode: scannedValue });
  } catch (error) {
    if ((error as Error)?.message === "Tote not found") {
      toast("No matching tote found", { variant: "error" });
    } else {
      console.error("Error getting tote by barcode", error);
      toast("Error finding tote by barcode", { variant: "error" });
    }
    void playErrorSound();
  }

  return tote;
}

export type ExpandedPickSessionItem = PickSessionItem & {
  cardIndex: number;
  toteId?: string;
};

/**
 * Groups expanded pick session items by variantId and bin.
 * Items are only combined if BOTH match.
 * Returns array with one entry per unique variant+bin combination.
 */

/**
 * Groups pick session items by variantId and bin.
 * Items are only combined if BOTH match.
 * Returns array with one entry per unique variant+bin combination.
 */
export function groupPickSessionItemsByVariantAndBin(
  items: PickSessionItem[],
): Array<{
  items: PickSessionItem[];
  displayItem: PickSessionItem;
}> {
  const grouped = new Map<
    string,
    { items: PickSessionItem[]; displayItem: PickSessionItem }
  >();

  for (const item of items) {
    const key = `${item.variantId || item.sku}-${item.bin ?? ""}`;

    const existing = grouped.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      grouped.set(key, { items: [item], displayItem: item });
    }
  }

  return Array.from(grouped.values());
}

export function groupExpandedItemsByVariantAndBin(
  items: ExpandedPickSessionItem[],
  pickSession: PickSession | undefined,
): Array<{
  item: ExpandedPickSessionItem;
  quantity: number;
  totalQuantity: number;
}> {
  const grouped = new Map<
    string,
    { item: ExpandedPickSessionItem; quantity: number; totalQuantity: number }
  >();

  for (const item of items) {
    const key = `${item.variantId || item.sku}-${item.bin ?? ""}`;

    // Find ALL pick session items with matching variantId and bin
    const matchingItems = pickSession?.items.filter(
      (pi) =>
        (pi.variantId || pi.sku) === (item.variantId || item.sku) &&
        (pi.bin ?? "") === (item.bin ?? ""),
    );

    // Sum totalQuantity from all matching items, subtracting out of stock quantity
    const totalQty =
      matchingItems?.reduce(
        (sum, pi) => sum + pi.totalQuantity - pi.quantityOutOfStock,
        0,
      ) || 1;

    const existing = grouped.get(key);
    if (existing) {
      existing.quantity += 1;
    } else {
      grouped.set(key, { item, quantity: 1, totalQuantity: totalQty });
    }
  }

  return Array.from(grouped.values());
}

export const expandItemsToIndividualCards = (items: PickSessionItem[]) => {
  const expandedItems: Array<ExpandedPickSessionItem> = [];
  const assignedItems: Array<ExpandedPickSessionItem> = [];
  const unassignedItems: Array<ExpandedPickSessionItem> = [];
  const itemsByTote: Record<string, Array<ExpandedPickSessionItem>> = {};

  if (items) {
    for (const item of items) {
      let globalCardIndex = 0;
      // Flatten totes into an array of toteIds (repeat by quantity)
      const toteAssignments: string[] = [];
      for (const [toteId, { quantity }] of Object.entries(
        item.quantityByTote ?? {},
      )) {
        for (let q = 0; q < quantity; q++) {
          toteAssignments.push(toteId);
        }
      }

      // Create cards for available items (total - out of stock)
      const availableQuantity = item.totalQuantity - item.quantityOutOfStock;

      for (let i = 0; i < availableQuantity; i++) {
        const card: ExpandedPickSessionItem = {
          ...item,
          cardIndex: globalCardIndex,
        };

        expandedItems.push(card);

        if (i < toteAssignments.length) {
          assignedItems.push(card);
          const toteId = toteAssignments[i];
          (itemsByTote[toteId] ??= []).push(card);
        } else {
          unassignedItems.push(card);
        }

        globalCardIndex++;
      }
    }
  }

  return { expandedItems, unassignedItems, assignedItems, itemsByTote };
};

export function getAvailableToteId({
  pickSession,
  scannedItem,
}: {
  pickSession: PickSession;
  scannedItem: ExpandedPickSessionItem;
}): string | null {
  if (pickSession.strategy === PickSessionStrategy.SINGLE_ITEM) {
    for (const item of pickSession.items) {
      for (const toteId of Object.keys(item.quantityByTote || {})) {
        return toteId;
      }
    }
  } else if (pickSession.strategy === PickSessionStrategy.MULTI_ITEM) {
    for (const item of pickSession.items) {
      if (item.fulfillmentGroupId === scannedItem.fulfillmentGroupId) {
        for (const toteId of Object.keys(item.quantityByTote || {})) {
          // For multi-item strategy, groups can be assigned to multiple totes, so only find the next one that isn't full
          if (!pickSession?.fullTotes?.includes(toteId)) {
            return toteId;
          }
        }
      }
    }
  }

  return null;
}

export function getUnassignedItemCountForFulfillmentGroup({
  pickSession,
  fulfillmentGroupId,
}: {
  pickSession: PickSession;
  fulfillmentGroupId: string;
}): number {
  return pickSession.items
    .filter((item) => item.fulfillmentGroupId === fulfillmentGroupId)
    .reduce((sum, item) => {
      const availableQuantity = item.totalQuantity - item.quantityOutOfStock;
      const assignedQuantity = Object.values(item.quantityByTote ?? {}).reduce(
        (toteSum, { quantity }) => toteSum + quantity,
        0,
      );
      return sum + availableQuantity - assignedQuantity;
    }, 0);
}

export function getFirstUnpickedExpandedItem({
  matchedItem,
}: {
  matchedItem: PickSessionItem;
}): ExpandedPickSessionItem {
  // Calculate how many items are still available to pick (excluding picked and out of stock)
  const availableQuantity =
    matchedItem.totalQuantity -
    matchedItem.quantityPicked -
    matchedItem.quantityOutOfStock;

  if (availableQuantity <= 0) {
    throw new Error(
      "All items have already been picked or marked unable to pick",
    );
  }

  const globalCardIndex = 0;

  // The unpicked index should start after the picked items
  const unpickedIndexInGroup = matchedItem.quantityPicked;
  const cardIndex = globalCardIndex + unpickedIndexInGroup;

  // Get tote assignment for this specific card
  const toteAssignments: string[] = [];
  for (const [toteId, { quantity }] of Object.entries(
    matchedItem.quantityByTote ?? {},
  )) {
    for (let q = 0; q < quantity; q++) {
      toteAssignments.push(toteId);
    }
  }

  const toteId = toteAssignments[unpickedIndexInGroup] || undefined;

  return { ...matchedItem, cardIndex, toteId };
}
